import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { NavLink, withRouter } from 'react-router-dom'
import {
    SYSTEM_COMPONENTS_IDS,
    SIDEBAR_MENU_ICONS,
    COMPONENTS_ROUTES,
    SIDEBAR_MENU,
    SIDEBAR_MENU_NAMES,
    HOTSWAP_PERMISSIONS
} from './constants'
import cs from 'classnames'
import { useSelector } from 'react-redux'
import { isFreeSoftwarePlan } from '../web-floor-plan/utils'
import packageJson from '../../../package.json'
import { UserPermissions } from '../../services/permissions'
import { Typography } from '@material-ui/core'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        position: 'absolute',
        top: 75,
        left: 0,
        width: '70px',
        height: 'calc(100vh - 75px)',
        margin: 0,
        backgroundColor: theme.background,
        borderRight: theme.border,
        transition: 'width 0.2s ease-in-out',
        zIndex: 1000,
        overflow: 'hidden',
        '&&:hover': {
            width: '246px',
            transition: 'width 0.2s ease-in-out',
            boxShadow: '0px 6px 8px 2px rgba(0, 0, 0, 0.1)'
        }
    },
    versionWrapper: {
        display: 'none',
        '$wrapper:hover &': {
            display: 'block'
        },
        marginLeft: 16,
        marginBottom: 8,
        marginTop: 'auto',
        color: theme.textSecondary,
        fontSize: 12
    },
    navLink: {
        display: 'flex',
        padding: '25px 21px',
        alignItems: 'center',
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
            backgroundColor: theme.secondary
        }
    },
    active: {
        backgroundColor: theme.primary,
        color: `${theme.textPrimaryReverted} !important`,
        fontWeight: '500 !important',
        '&:hover': {
            backgroundColor: `${theme.primary} !important`
        }
    },
    linkText: {
        display: 'none',
        color: theme.textPrimary,
        whiteSpace: 'nowrap',
        fontSize: 14,
        marginLeft: 10,
        fontWeight: '100',
        fontFamily: theme.fontFamily,
        '$wrapper:hover &': {
            display: 'block'
        }
    },
    linkIcon: {
        color: theme.textPrimary
    }
}))

function Sidebar(props) {
    const classes = useStyles()

    const { locale, user } = useSelector(state => state.main)
    const originalCustomerId = user?.original_customer_id

    const isActive = currentPath => {
        const locationParts = props.location.pathname.split('/')
        const linkPathParts = currentPath.split('/')

        return locationParts[1] === linkPathParts[1]
    }

    const {
        allowed_hotswap_request,
        allowed_update_service
    } = HOTSWAP_PERMISSIONS
    const hasHotSwapAccess =
        localStorage.getItem(allowed_hotswap_request) ||
        localStorage.getItem(allowed_update_service)

    return (
        <div className={classes.wrapper}>
            {Object.values(SIDEBAR_MENU).map(code => {
                let SidebarItemWithPermissions
                switch (code) {
                    case SIDEBAR_MENU.sharing:
                        SidebarItemWithPermissions = [
                            SYSTEM_COMPONENTS_IDS.locations,
                            SYSTEM_COMPONENTS_IDS.devices,
                            SYSTEM_COMPONENTS_IDS.reports
                        ].some(
                            componentCode =>
                                UserPermissions.customComponentId(componentCode)
                                    .create
                        )
                            ? SidebarItem
                            : null
                        break
                    case SIDEBAR_MENU.integrations:
                        if (
                            isFreeSoftwarePlan() ||
                            !UserPermissions.location(originalCustomerId).read
                        ) {
                            return null
                        }
                        SidebarItemWithPermissions = SidebarItem

                        break
                    case SIDEBAR_MENU.dashboard:
                        SidebarItemWithPermissions = [
                            SYSTEM_COMPONENTS_IDS.devices,
                            SYSTEM_COMPONENTS_IDS.reports
                        ].some(
                            componentCode =>
                                UserPermissions.customComponentId(componentCode)
                                    .read
                        )
                            ? SidebarItem
                            : null
                        break
                    case SIDEBAR_MENU.user_management:
                        if (!UserPermissions.isUserMgmtGranted()) return null
                        SidebarItemWithPermissions = SidebarItem
                        break
                    case SIDEBAR_MENU.swap_replacements:
                        if (!UserPermissions.isQCUser()) return null
                        SidebarItemWithPermissions = SidebarItem
                        break
                    case SIDEBAR_MENU.hot_swap:
                        if (!hasHotSwapAccess) {
                            return null
                        }
                        SidebarItemWithPermissions = SidebarItem
                        break
                    case SIDEBAR_MENU.hot_swap_admin:
                        if (!UserPermissions.isTraplinkedAdmin()) {
                            return null
                        }
                        SidebarItemWithPermissions = SidebarItem
                        break
                    default:
                        SidebarItemWithPermissions = UserPermissions.customComponentId(
                            SYSTEM_COMPONENTS_IDS[code]
                        ).read
                            ? SidebarItem
                            : null
                        break
                }

                if (!SidebarItemWithPermissions) {
                    return null
                }

                return (
                    <SidebarItemWithPermissions
                        key={code}
                        code={code}
                        isActive={isActive(COMPONENTS_ROUTES[code])}
                        locale={locale}
                    />
                )
            })}
            <div className={classes.versionWrapper}>
                ver.{packageJson.version}
            </div>
        </div>
    )
}

function SidebarItem(props) {
    const classes = useStyles()
    const { code, isActive, locale } = props

    const Icon = SIDEBAR_MENU_ICONS[code]

    return (
        <NavLink
            className={cs({
                [classes.navLink]: true,
                [classes.active]: isActive
            })}
            to={COMPONENTS_ROUTES[code]}
        >
            <div style={{ width: 20, height: 20 }}>
                <Icon
                    size={20}
                    className={cs({
                        [classes.linkIcon]: true,
                        [classes.active]: isActive
                    })}
                />
            </div>
            <Typography
                noWrap
                variant="inherit"
                className={cs({
                    [classes.linkText]: true,
                    [classes.active]: isActive
                })}
            >
                {SIDEBAR_MENU_NAMES(locale)[code]}
            </Typography>
        </NavLink>
    )
}

export default withRouter(Sidebar)
